import React, { useEffect, useMemo, useState } from "react";
import {
  fetchAuditTargets,
  fetchAudits,
  fetchAuditReport,
  generateAudit,
  updateAuditFinding,
} from "./auditApi";
import { downloadAuditHtml } from "./auditExportHtml";
import { trackReportDownload } from "@utils/activityTracker";

const SEVERITIES = [
  { key: "critical", label: "Crítico" },
  { key: "warning", label: "Atenção" },
  { key: "info", label: "Informativo" },
  { key: "positive", label: "Positivo" },
];

const brl = (v) =>
  v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const pct = (v) => (v == null ? "—" : `${v.toFixed(1).replace(".", ",")}%`);

const SummaryCards = ({ summary }) => (
  <div className="audit-summary">
    <div className="audit-card"><span>Orçado</span><b>{brl(summary.budget)}</b><small>indexado {brl(summary.budgetIndexed)}</small></div>
    <div className="audit-card"><span>Comprometido</span><b>{brl(summary.committed)}</b><small>{summary.budget ? pct((summary.committed / summary.budget) * 100) : "—"} do orçado</small></div>
    <div className="audit-card"><span>Pago</span><b>{brl(summary.paid)}</b><small>medido {brl(summary.measured)}</small></div>
    <div className="audit-card"><span>IDC</span><b>{summary.idc == null ? "—" : summary.idc.toFixed(2).replace(".", ",")}</b><small>físico {pct(summary.physicalActualPct)} × {pct(summary.physicalPlannedPct)} prev.</small></div>
    <div className="audit-card"><span>Tendência final</span><b>{brl(summary.finalCostTrend)}</b><small>orçamento {brl(summary.budget)}</small></div>
  </div>
);

const FindingCard = ({ reportId, finding, frozen, onChanged }) => {
  const [note, setNote] = useState(finding.curatorNote || "");
  const [saving, setSaving] = useState(false);

  const setStatus = async (status) => {
    setSaving(true);
    const res = await updateAuditFinding(reportId, finding.id, { status, curatorNote: note });
    setSaving(false);
    if (res?.finding) onChanged(res.finding);
  };

  return (
    <div className={`audit-finding audit-finding--${finding.severity} audit-finding--${finding.status}`}>
      <div className="audit-finding__head">
        <b>{finding.title}</b>
        {finding.valueIndexed != null && <span>{brl(finding.valueIndexed)}</span>}
        <em>{{ open: "Aberto", confirmed: "Confirmado", dismissed: "Descartado" }[finding.status]}</em>
      </div>
      <p>{finding.narrative}</p>
      {!frozen && (
        <div className="audit-finding__actions">
          <input
            type="text"
            placeholder="Nota do curador (opcional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button type="button" disabled={saving} onClick={() => setStatus("confirmed")}>Confirmar</button>
          <button type="button" disabled={saving} onClick={() => setStatus("dismissed")}>Descartar</button>
          {finding.status !== "open" && (
            <button type="button" disabled={saving} onClick={() => setStatus("open")}>Reabrir</button>
          )}
        </div>
      )}
    </div>
  );
};

const Audit = () => {
  const [targets, setTargets] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [buildingId, setBuildingId] = useState("");
  const [reports, setReports] = useState([]);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchAuditTargets().then((res) => setTargets(res?.companies ?? []));
  }, []);

  useEffect(() => {
    setReport(null);
    if (!buildingId) return setReports([]);
    fetchAudits(buildingId).then((res) => setReports(res?.reports ?? []));
  }, [buildingId]);

  const company = useMemo(() => targets.find((c) => String(c._id) === companyId), [targets, companyId]);

  const onGenerate = async () => {
    setGenerating(true);
    const res = await generateAudit(companyId, buildingId);
    setGenerating(false);
    if (res?.report) {
      setReport(res.report);
      const list = await fetchAudits(buildingId);
      setReports(list?.reports ?? []);
    }
  };

  const openReport = async (id) => {
    const res = await fetchAuditReport(id);
    if (res?.report) setReport(res.report);
  };

  const onFindingChanged = (updated) =>
    setReport((r) => ({ ...r, findings: r.findings.map((f) => (f.id === updated.id ? updated : f)) }));

  return (
    <div className="audit-page">
      <h2>Auditoria</h2>
      <p className="margin-bottom-16">
        Ferramenta interna — gera o snapshot de achados da obra na última medição, com curadoria antes do relatório oficial.
      </p>

      <div className="audit-controls">
        <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setBuildingId(""); }}>
          <option value="">Cliente…</option>
          {targets.map((c) => (
            <option key={String(c._id)} value={String(c._id)}>{c.name}</option>
          ))}
        </select>
        <select value={buildingId} onChange={(e) => setBuildingId(e.target.value)} disabled={!company}>
          <option value="">Obra…</option>
          {(company?.buildings ?? []).map((b) => (
            <option key={String(b._id)} value={String(b._id)}>{b.building_name}</option>
          ))}
        </select>
        <button type="button" disabled={!buildingId || generating} onClick={onGenerate}>
          {generating ? "Gerando…" : "Gerar auditoria"}
        </button>
      </div>

      {reports.length > 0 && (
        <table className="audit-list">
          <thead>
            <tr><th>Gerada em</th><th>Medição</th><th>Status</th><th>Achados</th><th /></tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={String(r._id)}>
                <td>{new Date(r.generated_at).toLocaleString("pt-BR")}</td>
                <td>{r.measurement_number} ({r.measurement_date})</td>
                <td>{r.status === "final" ? "Final" : "Rascunho"}</td>
                <td>
                  {SEVERITIES.map(({ key, label }) =>
                    r.findingsBySeverity?.[key] ? `${label}: ${r.findingsBySeverity[key]} ` : ""
                  )}
                </td>
                <td><button type="button" onClick={() => openReport(String(r._id))}>Abrir</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {report && (
        <div className="audit-report">
          <h3>
            Auditoria — medição {report.measurement_number} ({report.measurement_date})
            {report.status === "final" ? " · FINAL" : " · rascunho"}
          </h3>
          <div className="audit-export">
            <button
              type="button"
              onClick={() => {
                trackReportDownload("auditoria_medicao", {
                  format: "html",
                  measurementNumber: report.measurement_number,
                  buildingId,
                });
                downloadAuditHtml(report, {
                  companyName: company?.name ?? "",
                  buildingName:
                    company?.buildings.find((b) => String(b._id) === buildingId)?.building_name ?? "",
                });
              }}
            >
              Exportar HTML interativo
            </button>
          </div>
          <SummaryCards summary={report.summary} />
          {SEVERITIES.map(({ key, label }) => {
            const group = report.findings.filter((f) => f.severity === key);
            if (!group.length) return null;
            return (
              <section key={key} className="audit-severity-group">
                <h4>{label} ({group.length})</h4>
                {group.map((f) => (
                  <FindingCard
                    key={f.id}
                    reportId={String(report._id)}
                    finding={f}
                    frozen={report.status === "final"}
                    onChanged={onFindingChanged}
                  />
                ))}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Audit;
